import React from 'react';

import cn from 'classnames';
import { isFunction } from 'lodash';
import { string, node, oneOf, func, oneOfType } from 'prop-types';

import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faHandWave } from '@fortawesome/pro-regular-svg-icons/faHandWave';
import { faHourglassHalf } from '@fortawesome/pro-regular-svg-icons/faHourglassHalf';
import { faLightbulbOn } from '@fortawesome/pro-regular-svg-icons/faLightbulbOn';
import { faOctagonExclamation } from '@fortawesome/pro-regular-svg-icons/faOctagonExclamation';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';

import Text from 'components/Common/Text';

import s from './InfoNote.css';

const VARIANT_MAP = Object.freeze({
  tip: {
    className: s.tipContainer,
    icon: <FontAwesomeIcon icon={faLightbulbOn} />,
  },
  warning: {
    className: s.warningContainer,
    icon: <FontAwesomeIcon icon={faTriangleExclamation} />,
  },
  success: {
    className: s.successContainer,
    icon: <FontAwesomeIcon icon={faCircleCheck} />,
  },
  error: {
    className: s.errorContainer,
    icon: <FontAwesomeIcon icon={faOctagonExclamation} />,
  },
  pending: {
    className: s.pendingContainer,
    icon: <FontAwesomeIcon icon={faHourglassHalf} />,
  },
  info: {
    className: s.infoContainer,
    icon: <FontAwesomeIcon icon={faCircleInfo} />,
  },
  readOnly: {
    className: s.readOnlyContainer,
    icon: <FontAwesomeIcon icon={faHandWave} />,
  },
});

const SIZE_MAP = Object.freeze({
  large: s.largeContainer,
  medium: s.mediumContainer,
  small: s.smallContainer,
});

/**
 * Renders a component that fits the width of the parent container and should always be positioned at the top.
 *
 * @param {string|node} children - The primary content to display. This param can either be a string type or a custom element.
 * @param {string} containerClass - Custom styling classes that can be applied to the component.
 * @param {string} size - Sets the size of the component.
 * @param {string} variant - Changes the icon and color of the banner.
 * @param {string} className - Additional styling for the component
 * @param {node} customIcon - A react node that is used in place of the default icon
 * @param {func} onDismiss -  Callback fired when the component requests to be closed. When provided, a close icon button is displayed that triggers the callback when clicked.
 */
const InfoNote = ({
  children,
  containerClass,
  variant,
  size,
  className,
  customIcon,
  onDismiss,
}) => {
  const isText = typeof children === 'string';

  const getTextVariant = () => {
    if (size === 'large') {
      return 'bodySm';
    }
    return 'meta';
  };

  const renderContent = () => {
    if (isText) {
      return <Text variant={getTextVariant()}>{children}</Text>;
    }
    return <>{children}</>;
  };

  const isDismissible = isFunction(onDismiss);

  return (
    <div
      className={cn(
        className,
        s.container,
        VARIANT_MAP[variant].className,
        SIZE_MAP[size],
        containerClass,
      )}
    >
      {isDismissible ? <div /> : null}
      <Flexbox alignItems="center">
        {customIcon || VARIANT_MAP[variant].icon}
        {renderContent()}
      </Flexbox>
      {isDismissible ? (
        <FontAwesomeIcon
          className={s.clearIcon}
          onClick={onDismiss}
          icon={faXmark}
        />
      ) : null}
    </div>
  );
};

InfoNote.propTypes = {
  containerClass: string,
  children: oneOfType([node, string]).isRequired,
  variant: oneOf([
    'tip',
    'warning',
    'success',
    'error',
    'pending',
    'info',
    'readOnly',
  ]),
  size: oneOf(['large', 'medium', 'small']),
  className: string,
  customIcon: node,
  onDismiss: func,
};

InfoNote.defaultProps = {
  containerClass: '',
  variant: 'tip',
  size: 'medium',
  className: null,
  customIcon: null,
  onDismiss: undefined,
};

export default InfoNote;
